import {
  buildEmailShell,
  buildWhenWhereCardHtml,
  escapeHtml,
  BODY_FONT,
} from './emailShell';
import { formatEventDateRange } from './formatDateRange';

export interface AgendaSession {
  title: string;
  startTime: string; // formatted time, e.g. "8:30 AM"
  endTime: string;
  location: string | null;
  typeName: string;
  typeBackgroundColor: string;
  typeTextColor: string;
}

export interface AgendaDay {
  /** Formatted date heading, e.g. "Monday, June 15" */
  dateLabel: string;
  sessions: AgendaSession[];
}

export interface AgendaEmailData {
  recipientName: string | null;
  eventName: string;
  /** Raw YYYY-MM-DD start of event. Formatted to a readable range internally. */
  eventStartDate: string;
  /** Raw YYYY-MM-DD end of event. */
  eventEndDate: string;
  eventLocation: string | null;
  /**
   * Optional italic note shown above the schedule. Used to frame curated /
   * filtered agendas (e.g. "Your personalized schedule includes activities
   * and meals. Business sessions are private to investing principals.").
   * Null/undefined → no note rendered.
   */
  framingNote?: string | null;
  days: AgendaDay[];
}

export function buildAgendaEmailSubject(eventName: string): string {
  return `Your Agenda — ${eventName}`;
}

export function buildAgendaEmailHtml(data: AgendaEmailData): string {
  const {
    recipientName,
    eventName,
    eventStartDate,
    eventEndDate,
    eventLocation,
    framingNote,
    days,
  } = data;

  const greeting = recipientName
    ? `Dear ${escapeHtml(recipientName)},`
    : 'Hello,';

  const dateRange = formatEventDateRange(eventStartDate, eventEndDate);

  const eventDetailsCard = buildWhenWhereCardHtml(dateRange, eventLocation, 24);

  const framingNoteHtml = framingNote
    ? `<p style="margin:0 0 24px;font-size:14px;color:#666;font-family:${BODY_FONT};font-style:italic;line-height:1.6;">${escapeHtml(framingNote)}</p>`
    : '';

  let sessionsHtml = '';
  for (const day of days) {
    sessionsHtml += `
              <h3 style="margin:28px 0 12px;font-size:14px;color:#21376C;font-family:${BODY_FONT};font-weight:600;letter-spacing:0.06em;text-transform:uppercase;border-bottom:1px solid #e0e0e0;padding-bottom:8px;">
                ${escapeHtml(day.dateLabel)}
              </h3>`;

    for (const session of day.sessions) {
      // Title in a thin colored chip, full opacity. Time + location render
      // BELOW the chip in muted body text so they're legible regardless of
      // the chip's saturation. This is the legibility fix vs. cramming
      // everything inside the colored block.
      const locationLine = session.location
        ? `<span style="color:#888;"> &middot; ${escapeHtml(session.location)}</span>`
        : '';

      sessionsHtml += `
              <div style="margin:0 0 14px;">
                <div style="display:inline-block;padding:6px 12px;border-radius:4px;background:${session.typeBackgroundColor};">
                  <span style="font-size:14px;font-weight:600;color:${session.typeTextColor};font-family:${BODY_FONT};">${escapeHtml(session.title)}</span>
                </div>
                <p style="margin:4px 0 0;font-size:14px;color:#555;font-family:${BODY_FONT};">
                  ${escapeHtml(session.startTime)} &ndash; ${escapeHtml(session.endTime)}${locationLine}
                </p>
              </div>`;
    }
  }

  const bodyHtml = `
              <p style="margin:0 0 20px;font-size:16px;color:#333;font-family:${BODY_FONT};">${greeting}</p>
              <p style="margin:0 0 20px;font-size:16px;color:#333;font-family:${BODY_FONT};line-height:1.6;">Below is your personalized agenda for <strong>${escapeHtml(eventName)}</strong>.</p>

              ${eventDetailsCard}

              ${framingNoteHtml}

              ${sessionsHtml}

              <p style="margin:32px 0 0;font-size:14px;color:#888;font-family:${BODY_FONT};font-style:italic;border-top:1px solid #e0e0e0;padding-top:16px;line-height:1.6;">
                Open the attached <strong>.ics</strong> file to add these sessions to your calendar in Outlook, Google Calendar, or Apple Calendar.
              </p>`;

  return buildEmailShell({ eventName, bodyHtml });
}
